export type LandingWorkspaceAction =
  | "Contact"
  | "Pricing"
  | "Language"
  | "Download"
  | "Video";

export type LandingWorkspaceCommand = {
  id: number;
  action: LandingWorkspaceAction;
  toggleIfOpen: boolean;
};

export const LANDING_ACTION_COMPONENTS: Record<LandingWorkspaceAction, string[]> = {
  Contact: ["ContactForm", "Calendar"],
  Pricing: ["SubscriptionRequired"],
  Language: ["LanguagePicker"],
  Download: ["DownloadPanel"],
  Video: ["VideoLibrary"],
};

/**
 * Below this stage height (the viewport above the consent bar) the landing is short
 * on room. An open window then takes the footer's decorative provider rail for
 * itself (the rail collapses, the legal links stay). The hero, with no window
 * open, always keeps the rail.
 */
export const LANDING_SHORT_STAGE_HEIGHT_PX = 800;

export function isLandingShortStage(stageHeight: number): boolean {
  return stageHeight < LANDING_SHORT_STAGE_HEIGHT_PX;
}

export function isLandingFooterRailCollapsed({
  windowOpen,
  stageHeight,
}: {
  windowOpen: boolean;
  stageHeight: number;
}): boolean {
  return windowOpen && isLandingShortStage(stageHeight);
}

/** True for the actions that open a landing window (not sign-in). */
export function isLandingWorkspaceAction(value: string | null): value is LandingWorkspaceAction {
  return value !== null && Object.hasOwn(LANDING_ACTION_COMPONENTS, value);
}

type LandingDesktopWindowLayout = {
  /** Height before the window measures itself, or its fixed height. */
  preferredHeight: number;
  top: number;
  /** "content": the window measures its content and grows or shrinks to fit it. */
  fit?: "content";
};

export const LANDING_DESKTOP_WINDOW_LAYOUTS = {
  ContactForm: { preferredHeight: 440, top: 142 },
  SubscriptionRequired: { preferredHeight: 600, top: 142, fit: "content" },
  LanguagePicker: { preferredHeight: 480, top: 142, fit: "content" },
  DownloadPanel: { preferredHeight: 800, top: 142, fit: "content" },
  VideoLibrary: { preferredHeight: 600, top: 142, fit: "content" },
} as const satisfies Record<string, LandingDesktopWindowLayout>;

export type LandingDesktopWindowName = keyof typeof LANDING_DESKTOP_WINDOW_LAYOUTS;

/** Content-fit windows never shrink below this, so a slow first measure can't collapse them. */
export const LANDING_DESKTOP_MIN_CONTENT_HEIGHT = 240;

function getLandingDesktopWindowLayout(
  componentName: string | undefined,
): LandingDesktopWindowLayout | undefined {
  return componentName && Object.hasOwn(LANDING_DESKTOP_WINDOW_LAYOUTS, componentName)
    ? LANDING_DESKTOP_WINDOW_LAYOUTS[componentName as LandingDesktopWindowName]
    : undefined;
}

/** Whether the window measures its content and fits its height to it. */
export function isLandingContentFitWindow(componentName: string | undefined): boolean {
  return getLandingDesktopWindowLayout(componentName)?.fit === "content";
}

export function fitLandingDesktopWindowHeight(
  preferredHeight: number,
  top: number,
  safeBottom: number,
): number {
  return Math.max(1, Math.min(Math.floor(preferredHeight), Math.floor(safeBottom - top)));
}

/**
 * The fixed-height refit on viewport resize. Content-fit windows return null:
 * they re-measure themselves (see fitLandingDesktopContentHeight).
 */
export function fitLandingDesktopComponentHeight(
  componentName: string | undefined,
  safeBottom: number,
): number | null {
  const layout = getLandingDesktopWindowLayout(componentName);
  if (!layout || layout.fit === "content") return null;
  return fitLandingDesktopWindowHeight(layout.preferredHeight, layout.top, safeBottom);
}

/**
 * A content-fit window's height: its content's natural height plus the window's
 * own chrome, never past the footer and never below the minimum. Returns null
 * for windows that keep a fixed height.
 */
export function fitLandingDesktopContentHeight(
  componentName: string | undefined,
  measured: { contentHeight: number; chromeHeight: number },
  safeBottom: number,
  /** Where the window actually sits (it may have been moved or restored lower); defaults to its layout top. */
  top?: number,
): number | null {
  const layout = getLandingDesktopWindowLayout(componentName);
  if (layout?.fit !== "content") return null;
  const natural = Math.ceil(measured.contentHeight + Math.max(0, measured.chromeHeight));
  return fitLandingDesktopWindowHeight(
    Math.max(LANDING_DESKTOP_MIN_CONTENT_HEIGHT, natural),
    top ?? layout.top,
    safeBottom,
  );
}

/** "desktop": the fixed nav row above a wide viewport. "compact": phones and tablets under 1024px. */
export type LandingWindowLayout = "desktop" | "compact";

export type LandingWindowFrame = {
  position: { x: number; y: number };
  size: { width: number; height: number };
};

// Widest each window gets: Pricing shows its plans side by side and Videos the
// film beside its list; the rest are single-column panels.
const LANDING_WINDOW_MAX_WIDTHS: Record<LandingDesktopWindowName, number> = {
  ContactForm: 500,
  SubscriptionRequired: 960,
  LanguagePicker: 360,
  DownloadPanel: 400,
  VideoLibrary: 960,
};

const DESKTOP_WINDOW_MARGIN_PX = 24;
const DESKTOP_WINDOW_EDGE_PX = 16;
/** Compact windows hug the screen edge on phones and keep a gutter on tablets. */
export const LANDING_COMPACT_PHONE_MAX_WIDTH = 640;
const COMPACT_PHONE_MARGIN_PX = 8;
const COMPACT_TABLET_MARGIN_PX = 24;

/**
 * Where a landing window opens and how big it is. Desktop keeps each window's
 * place under the nav row (Contact on the left, Language and Download under
 * their controls on the right); compact centres every window under the header,
 * as wide as the screen allows. Heights stop at the footer boundary.
 */
export function getLandingWindowFrame(
  componentName: LandingDesktopWindowName,
  viewport: {
    layout: LandingWindowLayout;
    viewportWidth: number;
    safeBottom: number;
    /** Compact only: the window's top edge (just under the header and the window's control row). */
    compactTop?: number;
  },
): LandingWindowFrame {
  const layout = LANDING_DESKTOP_WINDOW_LAYOUTS[componentName];
  const maxWidth = LANDING_WINDOW_MAX_WIDTHS[componentName];
  const { viewportWidth, safeBottom } = viewport;

  if (viewport.layout === "compact") {
    const margin = viewportWidth < LANDING_COMPACT_PHONE_MAX_WIDTH
      ? COMPACT_PHONE_MARGIN_PX
      : COMPACT_TABLET_MARGIN_PX;
    const top = Math.round(viewport.compactTop ?? layout.top);
    const width = Math.max(1, Math.min(maxWidth, Math.floor(viewportWidth - margin * 2)));
    return {
      position: { x: Math.max(0, Math.floor((viewportWidth - width) / 2)), y: top },
      size: { width, height: fitLandingDesktopWindowHeight(layout.preferredHeight, top, safeBottom) },
    };
  }

  const isFixedWidthUtility = componentName === "LanguagePicker" || componentName === "DownloadPanel";
  const width = isFixedWidthUtility
    ? maxWidth
    : Math.min(maxWidth, viewportWidth - DESKTOP_WINDOW_MARGIN_PX * 2);
  const x = componentName === "ContactForm"
    ? 72
    : isFixedWidthUtility
      ? Math.max(DESKTOP_WINDOW_EDGE_PX, viewportWidth - width - DESKTOP_WINDOW_MARGIN_PX * 2)
      : Math.max(DESKTOP_WINDOW_EDGE_PX, Math.floor((viewportWidth - width) / 2));
  return {
    position: { x, y: layout.top },
    size: { width, height: fitLandingDesktopWindowHeight(layout.preferredHeight, layout.top, safeBottom) },
  };
}

/**
 * The most height a content-fit window can give its content: from where the window
 * sits down to the footer boundary, less its own chrome. Content taller than this
 * has to compact or scroll. Null for windows that keep a fixed height.
 */
export function getLandingDesktopContentRoom(
  componentName: string | undefined,
  chromeHeight: number,
  safeBottom: number,
  /** Where the window actually sits; defaults to its layout top. */
  top?: number,
): number | null {
  const layout = getLandingDesktopWindowLayout(componentName);
  if (layout?.fit !== "content") return null;
  return Math.max(0, Math.floor(safeBottom - (top ?? layout.top)) - Math.ceil(Math.max(0, chromeHeight)));
}

export function getVisibleLandingWorkspaceAction(
  components: ReadonlyArray<{
    name?: string;
    isMinimized?: boolean;
    zIndex?: number;
    score?: number;
  }>,
): LandingWorkspaceAction | null {
  const actionByPrimaryComponent = new Map(
    (Object.entries(LANDING_ACTION_COMPONENTS) as Array<[LandingWorkspaceAction, string[]]>).map(
      ([action, componentNames]) => [componentNames[0], action],
    ),
  );

  return components.reduce<{
    action: LandingWorkspaceAction | null;
    stackOrder: number;
    componentOrder: number;
  }>((selected, component, componentOrder) => {
    if (component.isMinimized) return selected;
    const action = component.name ? actionByPrimaryComponent.get(component.name) : undefined;
    if (!action) return selected;

    const stackOrder = component.zIndex ?? 10 + ((component.score ?? 5) * 10);
    if (
      stackOrder < selected.stackOrder ||
      (stackOrder === selected.stackOrder && componentOrder < selected.componentOrder)
    ) {
      return selected;
    }

    return { action, stackOrder, componentOrder };
  }, { action: null, stackOrder: Number.NEGATIVE_INFINITY, componentOrder: -1 }).action;
}
